import React, { useState, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "./customModal";
import showErrorToast from "./../toast/toast";
import { useSelector } from "react-redux";

const ReportingCategoryModel = ({
  isModalOpen,
  setIsModalOpen,
  onCategorySubmit,
  compID,
  categories = [],
  selectedCategory = null,
  showButton = true,
}) => {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [tempSelectedCategory, setTempSelectedCategory] = useState(selectedCategory);
  const isDarkMode = useDarkMode();

  const themeColor = useThemeColor();


  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((category) =>
        category && (category.rC_Name || category.rootCat_Name)?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  useEffect(() => {
    if (isModalOpen) {
      if (selectedCategory && (selectedCategory.rC_ID || selectedCategory.rootCat_ID)) {
        const matchingCategory = categories.find(cat => 
          (selectedCategory.rC_ID && selectedCategory.rC_ID === cat.rC_ID) ||
          (selectedCategory.rootCat_ID && selectedCategory.rootCat_ID === cat.rootCat_ID)
        );
        setTempSelectedCategory(matchingCategory || categories[0]);
      } else if (categories.length > 0) {
        setTempSelectedCategory(categories[0]);
      } else {
        setTempSelectedCategory(null);
      }
    }
  }, [selectedCategory, isModalOpen, categories]);

  const handleSubmit = () => {
    if (!tempSelectedCategory?.rC_ID && !tempSelectedCategory?.rootCat_ID) {
      showErrorToast("Please select a category.");
      return;
    }
    onCategorySubmit(tempSelectedCategory);
    setIsModalOpen(false);
    setSearchTerm("");
  };

  const handleCategorySelection = (category) => {
    setTempSelectedCategory(category);
  };

  return (
    <div>
      <CustomModal
        isOpen={isModalOpen}
        title="Select Reporting Category"
        subtitle="Choose a reporting category for your submission"
        onClose={() => {
          setIsModalOpen(false);
          setSearchTerm("");
          setTempSelectedCategory(null);
        }}
        onSubmit={handleSubmit}
        showButton={showButton}
        showSubmitButton={true}
        isCentered={true}
        submitButtonText="Done"
        modalSize="modal-lg"
        themeColor={themeColor}
      >
        <div className="mb-4">
          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-lg rounded-pill border-2"
              placeholder="🔍 Search reporting categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "45px",
                borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
                color: isDarkMode ? '#e2e8f0' : '#495057',
                transition: "all 0.3s ease"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = themeColor;
                e.target.style.boxShadow = `0 0 0 0.2rem ${themeColor}25`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
                e.target.style.boxShadow = "none";
              }}
            />
            <i 
              className="fa fa-search position-absolute"
              style={{
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                color: isDarkMode ? '#a0aec0' : '#6c757d'
              }}
            />
            {searchTerm && (
              <button
                className="btn btn-link position-absolute p-0"
                onClick={() => setSearchTerm("")}
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "16px",
                  color: isDarkMode ? '#a0aec0' : '#6c757d'
                }}
              >
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
        </div>

        <div 
          className="border rounded-3 p-3"
          style={{ 
            height: "350px", 
            overflowY: "auto",
            backgroundColor: isDarkMode ? '#2d3748' : '#f8f9fa',
            borderColor: isDarkMode ? '#4a5568' : '#e9ecef'
          }}
        >
          {filteredCategories.length > 0 ? (
            <div className="row g-3">
              {filteredCategories.filter(category => category && (category.rC_ID || category.rootCat_ID || category.rC_Name || category.rootCat_Name)).map((category) => {
                const isSelected = tempSelectedCategory && category && (
                  (tempSelectedCategory.rC_ID && tempSelectedCategory.rC_ID === category.rC_ID) || 
                  (tempSelectedCategory.rootCat_ID && tempSelectedCategory.rootCat_ID === category.rootCat_ID)
                );
                return (
                  <div key={category?.rC_ID || category?.rootCat_ID || Math.random()} className="col-md-6">
                    <div 
                      className={`card h-100 cursor-pointer border-2 transition-all`}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        borderRadius: "12px",
                        borderColor: isSelected ? themeColor : (isDarkMode ? '#4a5568' : '#e9ecef'),
                        backgroundColor: isSelected 
                          ? `${themeColor}15` 
                          : (isDarkMode ? '#2d3748' : '#ffffff')
                      }}
                      onClick={() => category && handleCategorySelection(category)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = themeColor;
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}15`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }}
                    >
                      <div className="card-body d-flex align-items-center justify-content-between p-3">
                        <div className="d-flex align-items-center">
                          <div 
                            className={`rounded-circle d-flex align-items-center justify-content-center me-3`}
                            style={{
                              width: "40px",
                              height: "40px",
                              fontSize: "16px",
                              transition: "all 0.3s ease",
                              backgroundColor: isSelected 
                                ? themeColor 
                                : (isDarkMode ? '#4a5568' : '#f8f9fa'),
                              color: isSelected 
                                ? '#ffffff' 
                                : (isDarkMode ? '#a0aec0' : '#6c757d')
                            }}
                          >
                            <i className={`fa ${isSelected ? 'fa-check' : 'fa-chart-bar'}`}></i>
                          </div>
                          <div>
                            <h6 
                              className={`mb-0 fw-semibold`}
                              style={{
                                color: isSelected 
                                  ? themeColor 
                                  : (isDarkMode ? '#e2e8f0' : '#212529')
                              }}
                            >
                              {category?.rC_Name || category?.rootCat_Name || 'Unknown Category'}
                            </h6>
                          </div>
                        </div>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={() => category && handleCategorySelection(category)}
                            id={`category-radio-${category?.rC_ID || category?.rootCat_ID || Math.random()}`}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              borderColor: isDarkMode ? '#718096' : '#ced4da',
                              backgroundColor: isSelected ? themeColor : 'transparent'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <i 
                  className="fa fa-search fa-3x mb-3"
                  style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                ></i>
                <h5 style={{ color: isDarkMode ? '#e2e8f0' : '#6c757d' }}>
                  No categories found
                </h5>
                <p style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                  {searchTerm.length > 0 ? `No categories match "${searchTerm}"` : "No categories available"}
                </p>
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default ReportingCategoryModel;